
import React, { useState, useRef } from 'react';
import './ResearchPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Button from '../../components/shared/Button';
import Card from '../../components/shared/Card';
import Badge from '../../components/shared/Badge';
import { useToast } from '../../context/ToastContext';

const SAMPLE_RESULTS = [
  {
    id: 1,
    title: 'The Future of AI in Enterprise Software',
    source: 'techcrunch.com',
    snippet: 'Artificial intelligence is rapidly transforming how enterprise software handles data, automation, and decision-making. Key players like OpenAI, Anthropic, and Google are competing to provide foundation models...',
    date: 'Feb 22, 2026',
    relevance: 98,
    type: 'Article',
  },
  {
    id: 2,
    title: 'LLM Benchmark Results Q1 2026',
    source: 'arxiv.org',
    snippet: 'A comprehensive evaluation of leading large language models across reasoning, coding, and instruction-following benchmarks reveals significant improvements in context utilization...',
    date: 'Feb 18, 2026',
    relevance: 95,
    type: 'Paper',
  },
  {
    id: 3,
    title: 'Dashboard Design Patterns for SaaS',
    source: 'smashingmagazine.com',
    snippet: 'Modern SaaS dashboards are evolving beyond simple data display. Information architecture research from Intercom and ServiceNow shows that grouping by user intent rather than feature count...',
    date: 'Feb 15, 2026',
    relevance: 91,
    type: 'Guide',
  },
  {
    id: 4,
    title: 'Real-time Collaboration in Web Apps',
    source: 'developer.mozilla.org',
    snippet: 'WebSockets and the new WebRTC data channels provide powerful primitives for building real-time collaborative experiences. This guide covers conflict resolution strategies...',
    date: 'Feb 10, 2026',
    relevance: 88,
    type: 'Docs',
  },
];

const SUGGESTED = [
  'Latest AI models benchmark 2026',
  'SaaS navigation best practices',
  'Enterprise dashboard UX patterns',
  'WebRTC video calling implementation',
];

type ResearchTab = 'search' | 'saved' | 'history';

export default function ResearchPage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<typeof SAMPLE_RESULTS>([]);
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState<ResearchTab>('search');
  const [summary, setSummary] = useState('');
  const { addToast } = useToast();

  const runSearch = (q?: string) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;
    setQuery(searchQuery);
    setLoading(true);
    setSearched(true);
    setResults([]);
    setSummary('');

    setTimeout(() => {
      setResults(SAMPLE_RESULTS);
      setLoading(false);
      // Simulate AI summary generation
      let text = '';
      const summaryText = `Based on ${SAMPLE_RESULTS.length} sources, the research on "${searchQuery}" reveals key trends in AI adoption, interface design, and real-time systems. Enterprise tools are converging around intent-based navigation and AI-assisted workflows, with foundation models from OpenAI and Anthropic leading benchmark results as of Q1 2026.`;
      let i = 0;
      const interval = setInterval(() => {
        i += 3;
        setSummary(summaryText.slice(0, i));
        if (i >= summaryText.length) clearInterval(interval);
      }, 20);
    }, 900);
  };

  return (
    <div className="research-page">
      <PageHeader
        title="Research"
        breadcrumb={['Studio', 'Research']}
        actions={
          <div className="research-tabs">
            {(['search', 'saved', 'history'] as ResearchTab[]).map(t => (
              <button
                key={t}
                className={`research-tab${activeTab === t ? ' research-tab--active' : ''}`}
                onClick={() => setActiveTab(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        }
      />

      {/* Search Bar */}
      <div className="research-searchbar">
        <div className="research-searchbar__inner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" style={{ color: 'var(--color-muted)', flexShrink: 0 }}>
            <circle cx="11" cy="11" r="6"/><path d="M20 20l-3.5-3.5"/>
          </svg>
          <input
            className="research-searchbar__input"
            placeholder="Search the web, summarize topics, fetch documents…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSearch()}
          />
          {query && (
            <button className="research-searchbar__clear" onClick={() => { setQuery(''); setResults([]); setSearched(false); setSummary(''); }}>✕</button>
          )}
          <Button size="sm" onClick={() => runSearch()} disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </Button>
        </div>
        {!searched && (
          <div className="research-suggestions">
            {SUGGESTED.map(s => (
              <button key={s} className="research-suggestion" onClick={() => runSearch(s)}>{s}</button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="research-loading">
          <div className="research-spinner" />
          <span>Fetching results and generating summary…</span>
        </div>
      )}

      {summary && (
        <Card title="AI Summary" actions={<Badge variant="ocean">AI Generated</Badge>} className="research-summary-card">
          <p className="research-summary-text">
            {summary}
            {summary.length < 280 && <span className="ai-cursor">▋</span>}
          </p>
          <div className="research-summary-actions">
            <Button variant="ghost" size="sm" onClick={() => addToast('success', 'Summary copied!')}>Copy</Button>
            <Button variant="ghost" size="sm" onClick={() => addToast('info', 'Saved to Assets')}>Save</Button>
            <Button variant="ghost" size="sm">Export PDF</Button>
          </div>
        </Card>
      )}

      {results.length > 0 && (
        <div className="research-results">
          <div className="research-results__header">
            <span className="research-results__count">{results.length} results · 0.42s</span>
            <select className="research-sort">
              <option>Sort: Relevance</option>
              <option>Sort: Date</option>
            </select>
          </div>
          <div className="research-results__list">
            {results.map(r => (
              <div key={r.id} className="research-result">
                <div className="research-result__header">
                  <div className="research-result__meta">
                    <span className="research-result__source">🌐 {r.source}</span>
                    <Badge variant={r.type === 'Paper' ? 'purple' : r.type === 'Docs' ? 'ocean' : 'default'}>{r.type}</Badge>
                    <span className="research-result__date">{r.date}</span>
                  </div>
                  <div className="research-result__relevance">
                    <span className="relevance-bar-bg">
                      <span className="relevance-bar-fill" style={{ width: `${r.relevance}%` }} />
                    </span>
                    <span className="relevance-val">{r.relevance}%</span>
                  </div>
                </div>
                <h3 className="research-result__title">{r.title}</h3>
                <p className="research-result__snippet">{r.snippet}</p>
                <div className="research-result__actions">
                  <Button variant="ghost" size="sm" onClick={() => addToast('info', `Opening: ${r.source}`)}>Open</Button>
                  <Button variant="ghost" size="sm" onClick={() => addToast('info', 'Summarized with AI')}>Summarize</Button>
                  <Button variant="ghost" size="sm" onClick={() => addToast('success', 'Saved to bookmarks')}>Save</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!searched && !loading && (
        <div className="research-empty-state">
          <div className="research-empty-state__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="48" height="48" style={{ color: 'var(--color-muted)', opacity: .4 }}>
              <circle cx="11" cy="11" r="6"/><path d="M20 20l-3.5-3.5"/>
            </svg>
          </div>
          <h3>Search the web with AI summaries</h3>
          <p>Fetch documents, summarize papers, and research any topic. Results are analyzed and summarized using your selected AI model.</p>
        </div>
      )}
    </div>
  );
}
